import React, { useState, useEffect, useRef } from 'react';

// Default architectural standards
const DEFAULT_STANDARDS = `MVC (Model-View-Controller):
- Controllers should only handle HTTP requests and responses
- Business logic belongs in models or services
- Views should be presentational only, no logic
- Database queries only in models
- No direct database access from controllers or views

Clean Architecture:
- Business logic must be independent of frameworks
- Use cases should not depend on UI or database
- Dependencies point inward (toward business logic)
- External concerns (DB, UI) in outer layers only
- No circular dependencies between layers

Layered Architecture:
- Presentation layer handles UI only
- Business layer contains core logic
- Data layer handles persistence only
- Each layer only depends on layer below
- No skipping layers in dependencies

Feature-Based Organization:
- Related functionality grouped by feature, not type
- Each feature folder is self-contained
- Shared utilities in /shared or /common only
- No cross-feature dependencies except through interfaces
- Feature folders should be relocatable

React Best Practices:
- Components should be presentational or container, not both
- Business logic in hooks or services, not components
- No data fetching in components (use hooks)
- Props drilling max 2 levels, use context beyond that
- No large component files (>200 lines)

API Architecture:
- Controllers handle routing only
- Business logic in services
- Data access in repositories
- Validation at controller level
- Error handling centralized in middleware`;

function StatusChecklistModal({ isOpen, onClose, issues: issuesProp = [], standardsViolations = [], isLoadingStandards = false, onScanStandards }) {
  const [view, setView] = useState('health'); // 'health' or 'rules'
  const [localIssues, setLocalIssues] = useState([]);
  const [showCopyToast, setShowCopyToast] = useState(false); // Toast for copy feedback
  const [copyToastMessage, setCopyToastMessage] = useState('Copied to clipboard!'); // Toast message
  const [expandedSections, setExpandedSections] = useState({
    largeFiles: false,
    crowdedFolders: false,
    unusedVariables: false
  });
  const onCloseRef = useRef(onClose);
  const prevIsOpenRef = useRef(isOpen);

  // Use localIssues if available, otherwise fall back to prop
  const issues = localIssues.length > 0 ? localIssues : issuesProp;

  // Reset to health view when modal opens (only on transition from closed to open)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal just opened - reset view to health
      setView('health');
      // Sync localIssues with prop when modal opens (if prop has issues and localIssues is empty)
      if (issuesProp.length > 0 && localIssues.length === 0) {
        setLocalIssues(issuesProp);
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, issuesProp, localIssues.length]);

  // Keep onClose ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Group issues by type
  const largeFiles = issues.filter(issue => issue.type === 'large_file');
  const crowdedFolders = issues.filter(issue => issue.type === 'crowded_folder');
  const unusedVariables = issues.filter(issue => issue.type === 'unused_variable');
  
  // Group unused variables by file for cleaner display
  const unusedVariablesByFile = unusedVariables.reduce((acc, issue) => {
    if (!acc[issue.file]) {
      acc[issue.file] = [];
    }
    acc[issue.file].push(issue);
    return acc;
  }, {});
  
  const codeQualityIssuesCount = issues.length;
  const standardsViolationsCount = standardsViolations?.length || 0;
  const totalIssues = codeQualityIssuesCount + standardsViolationsCount;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Standards scan state
  const [architectureScan, setArchitectureScan] = useState({
    hasRun: false,
    issues: [],
    lastScanTime: null
  });

  // Load detection rules from localStorage or use defaults
  const loadDetectionRules = () => {
    const defaults = {
      quickWins: {
        filesExceedingLines: { enabled: true, threshold: 500 },
        foldersExceedingFiles: { enabled: true, threshold: 20 },
        unusedCode: { enabled: true },
        circularDependencies: { enabled: false }
      },
      architecture: DEFAULT_STANDARDS
    };
    
    try {
      const saved = localStorage.getItem('codeQualityRules');
      if (saved) {
        const parsed = JSON.parse(saved);
        // If architecture is empty or missing, use default
        if (!parsed.architecture) {
          parsed.architecture = DEFAULT_STANDARDS;
        }
        return { ...defaults, ...parsed };
      }
    } catch (e) {
      console.error('Error loading rules from localStorage:', e);
    }
    return defaults;
  };

  const [detectionRules, setDetectionRules] = useState(loadDetectionRules);

  // Save detection rules to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('codeQualityRules', JSON.stringify(detectionRules));
    } catch (e) {
      console.error('Error saving rules to localStorage:', e);
    }
  }, [detectionRules]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleRuleToggle = (category, rule) => {
    setDetectionRules(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [rule]: {
          ...prev[category][rule],
          enabled: !prev[category][rule].enabled
        }
      }
    }));
  };

  const handleThresholdChange = (category, rule, value) => {
    const numValue = parseInt(value) || 0;
    setDetectionRules(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [rule]: {
          ...prev[category][rule],
          threshold: numValue
        }
      }
    }));
  };

  const handleArchitectureRulesChange = (value) => {
    setDetectionRules(prev => ({
      ...prev,
      architecture: value
    }));
  };

  const handleRunArchitectureScan = () => {
    // Placeholder: simulate scan completion
    setTimeout(() => {
      setArchitectureScan({
        hasRun: true,
        issues: [
          { id: 1, description: 'Mixed concerns detected in App.js' },
          { id: 2, description: 'Scattered feature logic across 5 files' }
        ],
        lastScanTime: Date.now()
      });
    }, 100);
  };

  const handleRescanArchitecture = () => {
    handleRunArchitectureScan();
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const minutes = Math.floor((Date.now() - timestamp) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes === 1) return '1 min ago';
    return `${minutes} min ago`;
  };

  // Copy helpers for issues
  const copyToClipboard = async (text, message = 'Copied to clipboard!') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyToastMessage(message);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Prompt generators (DRY - used by both individual and combined copy functions)
  const generateLargeFilesPrompt = (files) => {
    const limit = files[0]?.limit || 500;
    const fileList = files.map(f => `${f.file} (${f.lines} lines)`).join('\n');
    return `Current architecture smell: components mixing presentation, state management, and domain logic.

Files to refactor (each file must be ${limit} lines or fewer after refactoring):
${fileList}

Design a module structure that:
1. Separates concerns: /ui, /domain, /state
2. Makes dependencies flow one direction
3. Enables testing each layer independently
4. Keeps related code colocated
5. Ensures no single file exceeds ${limit} lines

Once you have the dependency graph, identify the specific files that need to be refactored and execute the refactoring.`;
  };

  const generateCrowdedFoldersPrompt = (folders) => {
    const limit = folders[0]?.limit || 20;
    const folderList = folders.map(f => `${f.folder} (${f.count} files)`).join('\n');
    return `Create subfolders to better organize the following crowded directories. Each folder should have no more than ${limit} files after reorganization:

${folderList}`;
  };

  const generateUnusedVariablesPrompt = (variables) => {
    const grouped = variables.reduce((acc, v) => {
      if (!acc[v.file]) acc[v.file] = [];
      acc[v.file].push(`  - ${v.variable} (line ${v.line})`);
      return acc;
    }, {});
    const fileList = Object.entries(grouped).map(([file, vars]) => `${file}:\n${vars.join('\n')}`).join('\n\n');
    return `Remove or fix the following unused variables:

${fileList}`;
  };

  const copyLargeFiles = (files) => {
    copyToClipboard(generateLargeFilesPrompt(files));
  };

  const copyCrowdedFolders = (folders) => {
    copyToClipboard(generateCrowdedFoldersPrompt(folders));
  };

  const copyUnusedVariables = (variables) => {
    copyToClipboard(generateUnusedVariablesPrompt(variables));
  };

  const copyAllIssues = (largeFiles, crowdedFolders, unusedVariables) => {
    const prompts = [];

    if (largeFiles.length > 0) {
      prompts.push(generateLargeFilesPrompt(largeFiles));
    }

    if (crowdedFolders.length > 0) {
      prompts.push(generateCrowdedFoldersPrompt(crowdedFolders));
    }

    if (unusedVariables.length > 0) {
      prompts.push(generateUnusedVariablesPrompt(unusedVariables));
    }

    copyToClipboard(prompts.join('\n\n---\n\n'), 'Copied all to clipboard!');
  };


  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        animation: 'fadeIn 0.15s ease-in'
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideUp 0.2s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {view === 'health' ? (
          <>
            {/* Content */}
        <div style={{
              padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
              {/* Issue count badge */}
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#dc2626',
                marginBottom: '20px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                🔴 {totalIssues} ISSUES DETECTED
              </div>

              {/* CODE QUALITY header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#111827',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  CODE QUALITY ({codeQualityIssuesCount}):
                </div>
                <button
                  onClick={() => copyAllIssues(largeFiles, crowdedFolders, unusedVariables)}
                  title="Copy all issues"
                  style={{
                    padding: '4px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6b7280" style={{ width: '18px', height: '18px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                  </svg>
                </button>
              </div>

              {/* Issues - Accordion Format */}
              {issues.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  {/* Large Files Accordion */}
                  {largeFiles.length > 0 && (
                    <div style={{
                      marginBottom: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}>
                      <div
                        style={{
                          padding: '12px 16px',
                          background: '#f9fafb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <div 
                          onClick={() => toggleSection('largeFiles')}
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            cursor: 'pointer',
                            flex: 1
                          }}
                        >
                          Found {largeFiles.length} file{largeFiles.length !== 1 ? 's' : ''} exceeding {largeFiles[0]?.limit || 500} line length
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyLargeFiles(largeFiles);
                            }}
                            title="Copy large files"
                            style={{
                              padding: '4px',
                              background: 'transparent',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#e5e7eb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6b7280" style={{ width: '16px', height: '16px' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                            </svg>
                          </button>
                          <div 
                            onClick={() => toggleSection('largeFiles')}
                            style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              transition: 'transform 0.2s ease',
                              transform: expandedSections.largeFiles ? 'rotate(180deg)' : 'rotate(0deg)',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            ▼
                          </div>
                        </div>
                      </div>
                      {expandedSections.largeFiles && (
                        <div style={{
                          padding: '8px 16px',
                          background: '#fff',
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}>
                          {largeFiles.map((issue, index) => (
                            <div
                              key={index}
                              style={{
                                padding: '8px 0',
                                borderBottom: index < largeFiles.length - 1 ? '1px solid #f3f4f6' : 'none',
                                fontSize: '13px',
                                color: '#111827'
                              }}
                            >
                              <code style={{
                                background: '#f3f4f6',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontFamily: 'monospace',
                                fontSize: '12px'
                              }}>{issue.file}</code> has <strong>{issue.lines}</strong> lines
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Crowded Folders Accordion */}
                  {crowdedFolders.length > 0 && (
                    <div style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}>
                      <div
                        style={{
                          padding: '12px 16px',
                          background: '#f9fafb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <div 
                          onClick={() => toggleSection('crowdedFolders')}
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            cursor: 'pointer',
                            flex: 1
                          }}
                        >
                          Found {crowdedFolders.length} folder{crowdedFolders.length !== 1 ? 's' : ''} exceeding {crowdedFolders[0]?.limit || 20} file count
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyCrowdedFolders(crowdedFolders);
                            }}
                            title="Copy crowded folders"
                            style={{
                              padding: '4px',
                              background: 'transparent',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#e5e7eb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6b7280" style={{ width: '16px', height: '16px' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                            </svg>
                          </button>
                          <div 
                            onClick={() => toggleSection('crowdedFolders')}
                            style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              transition: 'transform 0.2s ease',
                              transform: expandedSections.crowdedFolders ? 'rotate(180deg)' : 'rotate(0deg)',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            ▼
                          </div>
                        </div>
                      </div>
                      {expandedSections.crowdedFolders && (
                        <div style={{
                          padding: '8px 16px',
                          background: '#fff',
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}>
                          {crowdedFolders.map((issue, index) => (
                            <div
                              key={index}
                              style={{
                                padding: '8px 0',
                                borderBottom: index < crowdedFolders.length - 1 ? '1px solid #f3f4f6' : 'none',
                                fontSize: '13px',
                                color: '#111827'
                              }}
                            >
                              <code style={{
                                background: '#f3f4f6',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontFamily: 'monospace',
                                fontSize: '12px'
                              }}>{issue.folder}</code> has <strong>{issue.count}</strong> files
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Unused Variables Accordion */}
                  {unusedVariables.length > 0 && (
                    <div style={{
                      marginTop: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}>
                      <div
                        style={{
                          padding: '12px 16px',
                          background: '#f9fafb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <div 
                          onClick={() => toggleSection('unusedVariables')}
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            cursor: 'pointer',
                            flex: 1
                          }}
                        >
                          Found {unusedVariables.length} unused variable{unusedVariables.length !== 1 ? 's' : ''} across {Object.keys(unusedVariablesByFile).length} file{Object.keys(unusedVariablesByFile).length !== 1 ? 's' : ''}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyUnusedVariables(unusedVariables);
                            }}
                            title="Copy unused variables"
                            style={{
                              padding: '4px',
                              background: 'transparent',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#e5e7eb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6b7280" style={{ width: '16px', height: '16px' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                            </svg>
                          </button>
                          <div 
                            onClick={() => toggleSection('unusedVariables')}
                            style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              transition: 'transform 0.2s ease',
                              transform: expandedSections.unusedVariables ? 'rotate(180deg)' : 'rotate(0deg)',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            ▼
                          </div>
                        </div>
                      </div>
                      {expandedSections.unusedVariables && (
                        <div style={{
                          padding: '8px 16px',
                          background: '#fff',
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}>
                          {Object.entries(unusedVariablesByFile).map(([file, vars], fileIndex) => (
                            <div
                              key={file}
                              style={{
                                padding: '8px 0',
                                borderBottom: fileIndex < Object.keys(unusedVariablesByFile).length - 1 ? '1px solid #f3f4f6' : 'none'
                              }}
                            >
                              <code style={{
                                background: '#f3f4f6',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                display: 'inline-block',
                                marginBottom: '6px'
                              }}>{file}</code>
                              <div style={{ paddingLeft: '12px' }}>
                                {vars.map((v, varIndex) => (
                                  <div
                                    key={varIndex}
                                    style={{
                                      fontSize: '12px',
                                      color: '#6b7280',
                                      padding: '2px 0',
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '6px'
                                    }}
                                  >
                                    <span style={{ 
                                      color: '#9ca3af',
                                      fontFamily: 'monospace',
                                      fontSize: '11px',
                                      flexShrink: 0
                                    }}>
                                      L{v.line}
                                    </span>
                                    <span style={{ color: '#111827' }}>
                                      <strong>{v.variable}</strong>
                                      {v.message && !v.message.includes('Definition for rule') && (
                                        <span style={{ color: '#6b7280' }}> — {v.message}</span>
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Standards Scan Section - COMMENTED OUT: AI standards scanning temporarily disabled
              {(isLoadingStandards || standardsViolationsCount === 0) && (
                <div style={{
                  marginTop: issues.length > 0 ? '16px' : '0',
                  padding: '12px 16px',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  {isLoadingStandards ? (
                    <>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '13px',
                        color: '#6b7280'
                      }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #e5e7eb',
                          borderTopColor: '#8B5CF6',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Scanning standards...
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{
                        fontSize: '13px',
                        color: '#6b7280'
                      }}>
                        Standards scan not run
                      </div>
                      <button
                        onClick={() => {
                          if (onScanStandards) {
                            onScanStandards();
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: '#8B5CF6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#7c3aed';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#8B5CF6';
                        }}
                      >
                        Scan Standards
                      </button>
                    </>
                  )}
                </div>
              )}
              */}

              {/* Standards Violations Section - COMMENTED OUT: AI standards scanning temporarily disabled
              {standardsViolationsCount > 0 && !isLoadingStandards && (
                <>
                  {/* Divider before Standards * /}
                  <div style={{
                    height: '1px',
                    background: '#e5e7eb',
                    margin: '24px 0'
                  }} />

                  {/* STANDARDS header * /}
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '12px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}>
                    STANDARDS ({standardsViolationsCount}):
                  </div>

                  {/* Group violations by rule * /}
                  {(() => {
                    const violationsByRule = standardsViolations.reduce((acc, violation) => {
                      const rule = violation.rule || 'Unknown Rule';
                      if (!acc[rule]) {
                        acc[rule] = [];
                      }
                      acc[rule].push(violation);
                      return acc;
                    }, {});

                    return Object.entries(violationsByRule).map(([rule, violations]) => (
                      <div
                        key={rule}
                        style={{
                          marginBottom: '12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          style={{
                            padding: '12px 16px',
                            background: '#f9fafb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'background-color 0.15s ease'
                          }}
                        >
                          <div
                            onClick={() => toggleSection(`standards_${rule}`)}
                            style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#111827',
                              cursor: 'pointer',
                              flex: 1
                            }}
                          >
                            {rule} ({violations.length})
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFixStandardsViolations(rule, violations);
                              }}
                              disabled={fixingStandardsRule === rule}
                              style={{
                                padding: '4px 10px',
                                fontSize: '11px',
                                fontWeight: '600',
                                background: fixingStandardsRule === rule ? '#9ca3af' : '#8B5CF6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: fixingStandardsRule === rule ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s ease',
                                opacity: fixingStandardsRule === rule ? 0.7 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (fixingStandardsRule !== rule) {
                                  e.target.style.background = '#7c3aed';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (fixingStandardsRule !== rule) {
                                  e.target.style.background = '#8B5CF6';
                                }
                              }}
                            >
                              {fixingStandardsRule === rule ? 'Planning...' : 'Fix'}
                            </button>
                            <div
                              onClick={() => toggleSection(`standards_${rule}`)}
                              style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                transition: 'transform 0.2s ease',
                                transform: expandedSections[`standards_${rule}`] ? 'rotate(180deg)' : 'rotate(0deg)',
                                cursor: 'pointer',
                                padding: '4px'
                              }}
                            >
                              ▼
                            </div>
                          </div>
                        </div>
                        {expandedSections[`standards_${rule}`] && (
                          <div style={{
                            padding: '8px 16px',
                            background: '#fff',
                            maxHeight: '300px',
                            overflowY: 'auto'
                          }}>
                            {violations.map((violation, index) => (
                              <div
                                key={index}
                                style={{
                                  padding: '8px 0',
                                  borderBottom: index < violations.length - 1 ? '1px solid #f3f4f6' : 'none',
                                  fontSize: '13px',
                                  color: '#111827'
                                }}
                              >
                                <div style={{ marginBottom: '4px' }}>
                                  <code style={{
                                    background: '#f3f4f6',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px'
                                  }}>{violation.file}</code>
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      padding: '2px 6px',
                                      borderRadius: '3px',
                                      fontSize: '10px',
                                      fontWeight: '600',
                                      textTransform: 'uppercase',
                                      background: violation.severity === 'high' ? '#fef2f2' : '#fef9c3',
                                      color: violation.severity === 'high' ? '#dc2626' : '#ca8a04'
                                    }}
                                  >
                                    {violation.severity}
                                  </span>
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {violation.description}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </>
              )}
              */}

              {/* Edit Rules button */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '20px'
              }}>
                <button
                  onClick={() => setView('rules')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#111827';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  <span>⚙️</span>
                  edit rules
                </button>
              </div>

            </div>
          </>
        ) : (
          <>
            {/* Content */}
            <div style={{
              padding: '20px',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* Code Quality section */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '12px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  Code Quality:
                </div>
                
                {/* Files exceeding lines */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={detectionRules.quickWins.filesExceedingLines.enabled}
                    onChange={() => handleRuleToggle('quickWins', 'filesExceedingLines')}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#3B82F6'
                    }}
                  />
                  <span style={{ fontSize: '13px', color: '#111827' }}>
                    Flag Files exceeding
                  </span>
                  <input
                    type="number"
                    value={detectionRules.quickWins.filesExceedingLines.threshold}
                    onChange={(e) => handleThresholdChange('quickWins', 'filesExceedingLines', e.target.value)}
                    style={{
                      width: '60px',
                      padding: '4px 8px',
                      fontSize: '13px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      textAlign: 'center'
                    }}
                  />
                  <span style={{ fontSize: '13px', color: '#111827' }}>
                    lines
                  </span>
                </label>

                {/* Folders exceeding files */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={detectionRules.quickWins.foldersExceedingFiles.enabled}
                    onChange={() => handleRuleToggle('quickWins', 'foldersExceedingFiles')}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#3B82F6'
                    }}
                  />
                  <span style={{ fontSize: '13px', color: '#111827' }}>
                    Flag Folders exceeding
                  </span>
                  <input
                    type="number"
                    value={detectionRules.quickWins.foldersExceedingFiles.threshold}
                    onChange={(e) => handleThresholdChange('quickWins', 'foldersExceedingFiles', e.target.value)}
                    style={{
                      width: '60px',
                      padding: '4px 8px',
                      fontSize: '13px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      textAlign: 'center'
                    }}
                  />
                  <span style={{ fontSize: '13px', color: '#111827' }}>
                    files
                  </span>
                </label>

                {/* Unused code (imports + dead code) */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={detectionRules.quickWins.unusedCode?.enabled ?? true}
                    onChange={() => handleRuleToggle('quickWins', 'unusedCode')}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#3B82F6'
                    }}
                  />
                  <span style={{ fontSize: '13px', color: '#111827' }}>
                    Flag Unused imports & dead code
                  </span>
                </label>

                {/* Circular dependencies */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 0',
                  cursor: 'pointer'
                }}>
                <input
                  type="checkbox"
                    checked={detectionRules.quickWins.circularDependencies.enabled}
                    onChange={() => handleRuleToggle('quickWins', 'circularDependencies')}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#3B82F6'
                  }}
                />
                  <span style={{ fontSize: '13px', color: '#111827' }}>
                    Flag Circular dependencies
                  </span>
                </label>
              </div>

              {/* Standards section - COMMENTED OUT: AI standards scanning temporarily disabled
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '12px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  Standards:
                </div>
                <textarea
                  value={detectionRules.architecture}
                  onChange={(e) => handleArchitectureRulesChange(e.target.value)}
                  placeholder="Enter standards rules here..."
                  style={{
                    width: '100%',
                    minHeight: '150px',
                    padding: '12px',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    color: '#111827',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    resize: 'vertical',
                    lineHeight: '1.5',
                    backgroundColor: '#fff'
                  }}
                />
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '8px',
                  fontStyle: 'italic'
                }}>
                  Standards are checked automatically on startup
                </div>
              </div>
              */}

              {/* Save button */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '20px'
              }}>
                <button
                  onClick={() => setView('health')}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    background: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#3B82F6';
                  }}
                >
                  Save
                </button>
              </div>

            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Copy Toast Notification */}
      {showCopyToast && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: '#333',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 10001,
          animation: 'slideUp 0.2s ease-out'
        }}>
          {copyToastMessage}
        </div>
      )}
    </div>
  );
}

export default StatusChecklistModal;
